import type {DemandDataPerMaterial, AggregatedDemandData} from '@domain/material-demand'
import {
  ShippingType,
  getAggregatedDemandData,
  getTotalValueForShippingTypes
} from '@domain/material-demand'
import type {Material} from '@domain/materials'
import {dataTestId} from '@hconnect/uikit/src/common'
import {Column, DataTable, hpTheme} from '@hconnect/uikit/src/lib2'
import {useUpmPlantContext} from '@hconnect/upm'
import {SxProps, Theme, Typography, useTheme} from '@mui/material'
import {useMemo, useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {useCreateDemandOverride, usePlannerFeature, PlannerFeature} from '../../hooks'
import {useMaterialDemandEdit, useMaterialDemandFilters} from '../../providers'

import {MaterialDemandTableField} from './MaterialDemandTableField'
import {formatCombinedDemandValue} from './tableHelpers'

interface MaterialDemandTableEntry extends AggregatedDemandData {
  materialId: number | null
}

interface MaterialDemandTableProps {
  demandData: DemandDataPerMaterial
  materialsById: Record<string, Material> | undefined
}

export const MaterialDemandTable: React.FC<MaterialDemandTableProps> = ({
  demandData,
  materialsById
}) => {
  const {plantId: plantCode} = useUpmPlantContext()
  const {mutate: createDemandOverride} = useCreateDemandOverride()
  const {spacing} = useTheme()

  const isSeparateDemandPerType = usePlannerFeature(PlannerFeature.ThreeShippingTypes)

  const {
    t,
    i18n: {language}
  } = useTranslation(FEATURE_NAMESPACE)

  const {selectedMaterialIds, selectedDate, toggleMaterialId, selectAllMaterials} =
    useMaterialDemandFilters()
  const {isEditing} = useMaterialDemandEdit()

  const isMaterialSelected = useCallback(
    (material: MaterialDemandTableEntry) =>
      material.materialId !== null ? selectedMaterialIds.includes(material.materialId) : false,
    [selectedMaterialIds]
  )

  const data: MaterialDemandTableEntry[] = useMemo(() => {
    const totalEntry: MaterialDemandTableEntry = {
      ...getAggregatedDemandData(Object.values(demandData)),
      materialId: null
    }
    return [
      ...Object.entries(demandData).map(([materialId, demand]) => ({
        materialId: Number(materialId),
        ...demand
      })),
      totalEntry
    ]
  }, [demandData])

  const createOverride = useCallback(
    (
      {
        materialId,
        shippingType,
        quantity
      }: {materialId: number; shippingType: ShippingType; quantity: number},
      params: {onError: () => void; onSuccess: () => void}
    ) => {
      if (!selectedDate) return
      createDemandOverride(
        {plantCode, materialId, shippingType, quantity, date: selectedDate},
        params
      )
    },
    [createDemandOverride, plantCode, selectedDate]
  )

  const createOnEditShippingType = useCallback(
    (shippingType: ShippingType, createOverrideFn: typeof createOverride) =>
      (
        quantity: number,
        materialId: number,
        params: {onError: () => void; onSuccess: () => void}
      ) => {
        createOverrideFn({materialId, shippingType, quantity}, params)
      },
    []
  )

  const onEditShip = useMemo(
    () => createOnEditShippingType(ShippingType.Ship, createOverride),
    [createOnEditShippingType, createOverride]
  )
  const onEditTruck = useMemo(
    () => createOnEditShippingType(ShippingType.Truck, createOverride),
    [createOnEditShippingType, createOverride]
  )
  const onEditTrain = useMemo(
    () => createOnEditShippingType(ShippingType.Train, createOverride),
    [createOnEditShippingType, createOverride]
  )

  const fixedColumnWidth = spacing(24)
  const keyExtractor = useCallback(
    (row: MaterialDemandTableEntry) => String(row.materialId ?? 'total'),
    []
  )

  const shipmentForecastColumns: Column<MaterialDemandTableEntry>[] = useMemo(
    () => [
      {
        key: 'truckForecast',
        label: t('demand.shippingTypes.truck'),
        width: fixedColumnWidth,
        keyExtractor,
        customTemplate: (tableRow) => {
          const {merged, predictions, lastUpdates, materialId} = tableRow
          return (
            <MaterialDemandTableField
              key={`table-${materialId}-truckForecast`}
              mergedValue={merged[ShippingType.Truck]}
              predictedValue={predictions[ShippingType.Truck]}
              lastUpdate={lastUpdates[ShippingType.Truck]}
              materialId={materialId}
              // materialId is null for total row
              onEdit={materialId !== null ? onEditTruck : undefined}
            />
          )
        }
      },
      {
        key: 'shipForecast',
        label: t('demand.shippingTypes.ship'),
        width: fixedColumnWidth,
        keyExtractor,
        customTemplate: (tableRow) => {
          const {merged, predictions, lastUpdates, materialId} = tableRow
          return isSeparateDemandPerType ? (
            <MaterialDemandTableField
              key={`table-${materialId}-shipForecast`}
              mergedValue={merged[ShippingType.Ship]}
              predictedValue={predictions[ShippingType.Ship]}
              lastUpdate={lastUpdates[ShippingType.Ship]}
              materialId={materialId}
              // materialId is null for total row
              onEdit={materialId !== null ? onEditShip : undefined}
            />
          ) : undefined
        }
      },
      {
        key: 'trainForecast',
        label: t('demand.shippingTypes.train'),
        width: fixedColumnWidth,
        keyExtractor,
        customTemplate: (tableRow) => {
          const {merged, predictions, lastUpdates, materialId} = tableRow
          return isSeparateDemandPerType ? (
            <MaterialDemandTableField
              key={`table-${materialId}-trainForecast`}
              mergedValue={merged[ShippingType.Train]}
              predictedValue={predictions[ShippingType.Train]}
              lastUpdate={lastUpdates[ShippingType.Train]}
              materialId={materialId}
              // materialId is null for total row
              onEdit={materialId !== null ? onEditTrain : undefined}
            />
          ) : undefined
        }
      }
    ],
    [
      t,
      onEditShip,
      onEditTruck,
      onEditTrain,
      fixedColumnWidth,
      keyExtractor,
      isSeparateDemandPerType
    ]
  )

  const tableColumns: Column<MaterialDemandTableEntry>[] = useMemo(
    () => [
      {
        key: 'materialId',
        label: t('common.type'),
        width: 'auto',
        columnSx: {maxWidth: ({spacing}) => spacing(40)},
        keyExtractor,
        customTemplate: ({materialId}) => {
          if (materialId === null) {
            return <Typography variant="subtitle1">{t('common.total')}</Typography>
          }
          return materialsById?.[materialId].name ?? t('common.loading')
        }
      },
      {
        key: 'actuals',
        label: t('demand.actualSales'),
        keyExtractor,
        width: fixedColumnWidth,
        customTemplate: ({actuals}) => formatCombinedDemandValue(actuals, language)
      },
      {
        key: 'salesForecast',
        label: t('demand.uploadedForecast'),
        keyExtractor,
        width: fixedColumnWidth,
        customTemplate: ({salesForecast}) => formatCombinedDemandValue(salesForecast, language)
      },
      {
        key: 'preOrders',
        label: t('demand.preOrder'),
        keyExtractor,
        width: fixedColumnWidth,
        customTemplate: ({preOrders}) => formatCombinedDemandValue(preOrders, language)
      },
      ...(isSeparateDemandPerType ? shipmentForecastColumns : []),
      {
        key: 'totalForecast',
        label: isSeparateDemandPerType
          ? t('demand.totalForecast')
          : t('demand.demandLegend.statisticalForecast'),
        keyExtractor,
        width: fixedColumnWidth,
        customTemplate: (tableRow) => {
          const {merged, predictions, lastUpdates, materialId} = tableRow
          const mergedValue = isSeparateDemandPerType
            ? getTotalValueForShippingTypes(merged)
            : merged[ShippingType.Truck]
          const predictedValue = isSeparateDemandPerType
            ? getTotalValueForShippingTypes(predictions)
            : predictions[ShippingType.Truck]
          return (
            <MaterialDemandTableField
              key={`table-${materialId}-totalForecast`}
              mergedValue={mergedValue ?? undefined}
              predictedValue={predictedValue ?? undefined}
              // we should hide last updates when there are 3 shipping types
              lastUpdate={isSeparateDemandPerType ? undefined : lastUpdates[ShippingType.Truck]}
              materialId={materialId}
              // materialId is null for total row
              onEdit={materialId !== null && !isSeparateDemandPerType ? onEditTruck : undefined}
            />
          )
        }
      }
    ],
    [
      isSeparateDemandPerType,
      language,
      materialsById,
      shipmentForecastColumns,
      onEditTruck,
      keyExtractor,
      fixedColumnWidth,
      t
    ]
  )

  const onRowClick = useCallback(
    (_: React.MouseEvent, row: MaterialDemandTableEntry) => {
      // excluding total row
      const materialId = row.materialId
      if (materialId === null || isEditing) return
      const isSelected = isMaterialSelected(row)
      // if all materials are selected, deselect all
      if (selectedMaterialIds.length === data.length - 2 && !isSelected) {
        selectAllMaterials()
      } else {
        toggleMaterialId(materialId)
      }
    },
    [toggleMaterialId, selectedMaterialIds, data, selectAllMaterials, isMaterialSelected, isEditing]
  )

  const rowSx = useCallback(
    (row: MaterialDemandTableEntry): SxProps<Theme> => ({
      height: ({spacing}) => spacing(7.5),
      ...(isMaterialSelected(row) ? {backgroundColor: ({palette}) => palette.action.selected} : {}),
      '& td': {
        ...hpTheme.typography.body1,
        // styles for Total row
        ...(row.materialId === null
          ? {
              borderTop: ({palette}) => `2px solid ${palette.text.primary}`,
              pt: 2,
              borderBottom: 'none',
              fontWeight: hpTheme.typography.h4.fontWeight
            }
          : {})
      }
    }),
    [isMaterialSelected]
  )

  return (
    <DataTable
      {...dataTestId('material_demand_table')}
      columns={tableColumns}
      data={data}
      onRowClick={onRowClick}
      emptyMessage={t('demand.noData')}
      keyExtractor={keyExtractor}
      rowSx={rowSx}
    />
  )
}
